import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import ButtonLink from '@/common/components/ButtonLink'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { useCurrentUser } from '@/common/hooks/useCurrentUser'
import { MessageData } from '@/common/types/support/message.type'
import { cn } from '@/common/utils/cn'
import { FormMessage } from '@/features/support/internal/components/FormMessage'
import { Message } from '@/features/support/internal/components/Message'
import { supportStatusStrategy } from '@/features/support/internal/constants/supportStatusOptions.constant'
import { supportTicketColumns } from '@/features/support/internal/constants/supportTicketColumns.constant'
import { userColumns } from '@/features/support/internal/constants/userColumns.constant'
import { useSupportTicket } from '@/features/support/internal/hooks/useSupportTicket'
import { useSupportTicketMessages } from '@/features/support/internal/hooks/useSupportTicketMessages'
import { InternalSupportTicketsTransformer } from '@/features/support/internal/services/internalSupportTicketsTransformer'
import { UserRoundIcon } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'

const InternalSupportShow = () => {
  const { id: supportTicketId } = useParams()

  const { data: user } = useCurrentUser({})

  const {
    data: supportTicketData,
    isPending: supportTicketDataIsPending,
    error: supportTicketDataError = {} as any,
    refetch: refetchSupportTicket,
  } = useSupportTicket({
    id: supportTicketId || '',
  })

  const { data: supportTicketMessagesData } = useSupportTicketMessages<MessageData[]>({
    id: supportTicketId || '',
  })

  const disableUpdateButton = supportTicketDataIsPending || supportTicketDataError?.status === 403

  return (
    <>
      {supportTicketDataIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={supportTicketDataError?.status}
          refetchFunction={refetchSupportTicket}
        >
          <div className="flex justify-between items-center mb-6 text-white-800">
            <div className="flex gap-4 items-center">
              <GoBackButton fallback="/internal-support" searchLastRouteFromFallback />

              <div className="flex gap-2 items-center">
                <h2 className="text-2xl font-bold">{supportTicketData?.reference_number}</h2>

                <div
                  data-testid="status_badge"
                  className={cn(
                    'px-2 py-1 rounded-sm',
                    supportTicketData?.status &&
                      ['pending', 'awaiting_tcard_response', 'not_responded', 'in_progress'].includes(
                        supportTicketData?.status,
                      ) &&
                      'bg-yellow-op-2',
                    supportTicketData?.status &&
                      ['awaiting_customer_response', 'closed'].includes(supportTicketData?.status) &&
                      'bg-blue-200',
                  )}
                >
                  {supportStatusStrategy[supportTicketData?.status as keyof typeof supportStatusStrategy]}
                </div>
              </div>
            </div>

            <Link
              to={disableUpdateButton ? '' : `/internal-support/${supportTicketId}/edit`}
              className={disableUpdateButton ? 'cursor-default' : ''}
            >
              <Button
                operationKlassToCheckPermission="Admin::Support::Tickets::Update"
                type="submit"
                name="edit_external_support_ticket"
                className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                disabled={disableUpdateButton}
              >
                Editar
              </Button>
            </Link>
          </div>

          <Box className="flex flex-col gap-y-6 text-white-800 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
            <div className="flex flex-col gap-y-4">
              <p className="text-lg font-bold">Autor</p>

              <ButtonLink
                linkTo={`/users/${supportTicketData?.creator.id}`}
                operationKlassToCheckPermission='Admin::Users::Show'
                dataTestId="creator_clickable_link"
              >
                <div className="flex items-center gap-x-2">
                  <ImageWithPlaceholder
                    url={supportTicketData?.creator?.profile_picture?.url}
                    alt={supportTicketData?.creator?.full_name}
                    placeholderIcon={UserRoundIcon}
                    className="size-8"
                    iconClassName="size-6"
                  />

                  <div className="flex items-center gap-x-1">
                    <p className="text-sm font-bold">{supportTicketData?.creator?.full_name}</p>

                    <p className="text-sm font-light italic">(Agente Tcard)</p>
                  </div>
                </div>
              </ButtonLink>

              <div className="flex w-full flex-col gap-y-4">
                <p className="text-lg font-bold">Usuários vinculados</p>

                <Table
                  className={cn('max-h-[14.75rem]', {
                    'min-h-14': supportTicketData?.user_tickets && supportTicketData?.user_tickets?.length > 0,
                  })}
                  columns={userColumns(true)}
                  rows={
                    InternalSupportTicketsTransformer.usersTableTransformer()(
                      supportTicketData?.user_tickets?.map(({ user, relation_type }) => ({
                        ...user,
                        relationType: relation_type,
                      })) || [],
                    ) || []
                  }
                  showEmptyTable
                  rowEmptyValue="--"
                  page={1}
                  totalPages={1}
                  isLoadingData={false}
                />
              </div>

              <TextField
                label="Motivo"
                name="reason"
                value={supportTicketData?.reason.display_name}
                valueClassName="text-white-800"
                disabled
              />

              <div className="flex w-full flex-col gap-y-4">
                <p className="text-lg font-bold">Chamado vinculados</p>

                <Table
                  className={cn('max-h-[14.75rem]', {
                    'min-h-14':
                      supportTicketData?.outgoing_ticket_linkings &&
                      supportTicketData?.outgoing_ticket_linkings?.length > 0,
                  })}
                  columns={supportTicketColumns}
                  rows={InternalSupportTicketsTransformer.supportTicketsTableTransformer()({
                    supportTickets:
                      supportTicketData?.outgoing_ticket_linkings?.map((linking) => linking.target_ticket) || [],
                    actionType: 'link',
                  })}
                  showEmptyTable
                  rowEmptyValue="--"
                  page={1}
                  totalPages={1}
                  isLoadingData={false}
                />
              </div>

              <TextField
                label="Assunto"
                name="subject_line"
                value={supportTicketData?.subject_line}
                valueClassName="text-white-800"
                disabled
              />
            </div>

            {supportTicketMessagesData?.map((message) => (
              <Message key={message.id} message={message} margin={message.creator.id === user?.id ? 'left' : 'right'} />
            ))}

            {!!supportTicketData && supportTicketData?.status !== 'closed' && (
              <FormMessage supportTicketId={supportTicketData?.id} />
            )}
          </Box>
        </ErrorMessageWrapper>
      )}
    </>
  )
}

export default InternalSupportShow
